import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import appleIcon from '../../Assets/appleIcon.png';
import gpayIcon from '../../Assets/googleIcon.png';
import './WalletPaymentPendingModal.css';

/**
 * WalletPaymentPendingModal — IOO-275 §4
 *
 * Shown immediately after Apple Pay / Google Pay initiatePayment() succeeds.
 * Displays a self-managed countdown from paymentTimeoutMs (= extension_duration_seconds × 1000).
 * The countdown resets every time isOpen transitions false → true.
 *
 * Props:
 *   isOpen           {bool}    — controls visibility
 *   paymentMethod    {string}  — "apple" | "gpay"
 *   paymentTimeoutMs {number}  — extension_duration_seconds × 1000 (from backend settings)
 *   totalAmount      {number}  — e.g. 95.00
 *   bookingDetails   {object}  — { date, time, showAppoinmentData }
 *   isConfirming     {bool}    — true while /check-stripe-payment is in-flight
 *   onPaymentTimeout {func}    — called when internal countdown reaches 0
 */
const WalletPaymentPendingModal = ({
  isOpen,
  paymentMethod,
  paymentTimeoutMs,
  totalAmount,
  bookingDetails,
  isConfirming,
  onPaymentTimeout,
}) => {
  // Internal countdown — resets to full duration every time modal opens
  const fullSeconds = Math.round((paymentTimeoutMs || 300000) / 1000);
  const [paymentSecondsLeft, setPaymentSecondsLeft] = useState(fullSeconds);

  useEffect(() => {
    if (!isOpen) {
      // Reset to full duration so next open starts fresh
      setPaymentSecondsLeft(fullSeconds);
      return;
    }

    // Start a fresh countdown every time modal opens
    setPaymentSecondsLeft(fullSeconds);
    const interval = setInterval(() => {
      setPaymentSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (onPaymentTimeout) onPaymentTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, fullSeconds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ──────────────────────────────────────────
  const minutes = Math.floor(paymentSecondsLeft / 60);
  const seconds = paymentSecondsLeft % 60;
  const timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const timerClass =
    paymentSecondsLeft > 60 ? 'safe' :
      paymentSecondsLeft > 30 ? 'warn' : 'urgent';

  const isUrgent = paymentSecondsLeft <= 30;

  // ── Booking summary data ────────────────────────────────────
  const serviceName =
    bookingDetails?.showAppoinmentData?.showAppoinmentData?.clinic?.productCategory?.title ||
    bookingDetails?.showAppoinmentData?.clinic?.productCategory?.title ||
    'Appointment';

  const bookingDate = bookingDetails?.date || '—';
  const bookingTime = bookingDetails?.time || '—';
  const amountDisplay = totalAmount != null ? `£ ${Number(totalAmount).toFixed(2)}` : '—';

  // ── Payment method meta ─────────────────────────────────────
  const isApple = paymentMethod === 'apple';
  const methodIcon = isApple ? appleIcon : gpayIcon;
  const methodLabel = isApple ? 'Apple Pay' : 'Google Pay';

  return (
    <Modal
      className="wallet-modal"
      open={isOpen}
      centered
      maskClosable={false}
      closable={false}
      footer={null}
      width={440}
    >
      {/* ── Header ─────────────────────────────────────── */}
      <div className="wpm-header">
        <div className="wpm-icon-wrap">
          <img src={methodIcon} alt={methodLabel} style={{ width: 48, height: 48, objectFit: 'contain' }} />
        </div>

        <p className="wpm-title">Complete Your Payment</p>

        <p className="wpm-subtitle">
          Your booking slot is reserved.<br />
          Complete your payment using <strong>{methodLabel}</strong>.
        </p>
      </div>

      {/* ── Countdown timer card ────────────────────────── */}
      <div className={`wpm-timer-card${isUrgent ? ' urgent' : ''}`}>
        <span className="wpm-timer-label">Complete payment within</span>
        <div className={`wpm-timer-value ${timerClass}`}>{timeString}</div>
      </div>

      {/* ── Booking summary card ────────────────────────── */}
      <div className="wpm-summary">
        <div className="wpm-summary-row">
          <span className="wpm-summary-label">Service</span>
          <span className="wpm-summary-value">{serviceName}</span>
        </div>
        <div className="wpm-summary-row">
          <span className="wpm-summary-label">Date</span>
          <span className="wpm-summary-value">{bookingDate}</span>
        </div>
        <div className="wpm-summary-row">
          <span className="wpm-summary-label">Time</span>
          <span className="wpm-summary-value">{bookingTime}</span>
        </div>
        <div className="wpm-summary-row">
          <span className="wpm-summary-label">Amount</span>
          <span className="wpm-summary-value">{amountDisplay}</span>
        </div>
      </div>

      {/* ── Waiting / confirming state ──────────────────── */}
      <div className="wpm-waiting">
        <div className="wpm-spinner" />
        {isConfirming ? (
          <p className="wpm-confirming-text">Confirming your payment…</p>
        ) : (
          <p className="wpm-waiting-text">Waiting for payment confirmation…</p>
        )}
      </div>
    </Modal>
  );
};

export default WalletPaymentPendingModal;
